import { useState, useEffect, useCallback } from 'react';
import { toast } from '@/components/ui/sonner';
import { Fuel, Plus, Download, ChevronLeft, ChevronRight, Search, Trash2, TrendingUp, Filter, Users, Info, AlertTriangle, RefreshCw, X } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import * as XLSX from 'xlsx';

const FuelTracking = ({ user, activeModule = 'b2b' }) => {
  const [activeTab, setActiveTab] = useState('logs');
  // Logs state
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotalPages, setLogsTotalPages] = useState(0);
  const [logsLoading, setLogsLoading] = useState(false);

  // Date filters
  const [viewMode, setViewMode] = useState('month');
  const [currentDate, setCurrentDate] = useState(new Date());

  // Add fuel form
  const [showAddForm, setShowAddForm] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState('');
  const [missions, setMissions] = useState([]);
  const [selectedMission, setSelectedMission] = useState('');
  const [montantTtc, setMontantTtc] = useState('');
  const [litres, setLitres] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState(null);
  const [addSuccess, setAddSuccess] = useState(null);

  // Stats
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // ======= GROUPED FUEL STATE =======
  const [groups, setGroups] = useState([]);
  const [groupsTotal, setGroupsTotal] = useState(0);
  const [groupsPage, setGroupsPage] = useState(1);
  const [groupsTotalPages, setGroupsTotalPages] = useState(0);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [allVehicles, setAllVehicles] = useState([]);
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [groupMontant, setGroupMontant] = useState('');
  const [groupDate, setGroupDate] = useState(new Date().toISOString().split('T')[0]);
  const [groupLoading, setGroupLoading] = useState(false);
  const [groupError, setGroupError] = useState(null);
  const [groupSuccess, setGroupSuccess] = useState(null);
  const [groupStats, setGroupStats] = useState(null);

  // ======= ALERTS STATE =======
  const [alerts, setAlerts] = useState([]);
  const [alertsTotal, setAlertsTotal] = useState(0);
  const [alertsPage, setAlertsPage] = useState(1);
  const [alertsTotalPages, setAlertsTotalPages] = useState(0);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanResult, setScanResult] = useState(null);

  // Get date range from viewMode + currentDate
  const getDateRange = useCallback(() => {
    const d = new Date(currentDate);
    let start, end;
    if (viewMode === 'day') {
      const ds = d.toISOString().split('T')[0];
      return { start_date: ds, end_date: ds };
    } else if (viewMode === 'week') {
      const day = d.getDay();
      const diff = d.getDate() - day + (day === 0 ? -6 : 1);
      start = new Date(d);
      start.setDate(diff);
      end = new Date(start);
      end.setDate(start.getDate() + 6);
    } else {
      start = new Date(d.getFullYear(), d.getMonth(), 1);
      end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    }
    return {
      start_date: start.toISOString().split('T')[0],
      end_date: end.toISOString().split('T')[0]
    };
  }, [viewMode, currentDate]);

  const getDateLabel = () => {
    const d = new Date(currentDate);
    if (viewMode === 'day') {
      return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    } else if (viewMode === 'week') {
      const day = d.getDay();
      const diff = d.getDate() - day + (day === 0 ? -6 : 1);
      const start = new Date(d);
      start.setDate(diff);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return `${start.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} - ${end.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}`;
    } else {
      return d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    }
  };

  const navigate = (direction) => {
    const d = new Date(currentDate);
    if (viewMode === 'day') d.setDate(d.getDate() + direction);
    else if (viewMode === 'week') d.setDate(d.getDate() + (7 * direction));
    else d.setMonth(d.getMonth() + direction);
    setCurrentDate(d);
    setLogsPage(1);
  };

  // Fetch logs
  const fetchLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const { start_date, end_date } = getDateRange();
      const res = await axios.get(`${API}/fuel/logs`, {
        params: { start_date, end_date, page: logsPage, page_size: 50 },
        withCredentials: true
      });
      setLogs(res.data.logs || []);
      setLogsTotal(res.data.total || 0);
      setLogsTotalPages(res.data.total_pages || 0);
    } catch (err) {
      console.error('Failed to fetch fuel logs:', err);
    } finally {
      setLogsLoading(false);
    }
  }, [getDateRange, logsPage]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const { start_date, end_date } = getDateRange();
      const res = await axios.get(`${API}/fuel/stats`, {
        params: { start_date, end_date },
        withCredentials: true
      });
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch fuel stats:', err);
    } finally {
      setStatsLoading(false);
    }
  }, [getDateRange]);

  // Fetch drivers
  const fetchDrivers = async () => {
    try {
      const res = await axios.get(`${API}/users`, {
        params: { include_inactive: false },
        withCredentials: true
      });
      const employees = (res.data || []).filter(u => u.role === 'employee' && u.active !== false);
      setDrivers(employees);
    } catch (err) {
      console.error('Failed to fetch drivers:', err);
    }
  };

  // Fetch missions for selected driver
  const fetchMissions = async (driverId) => {
    try {
      const res = await axios.get(`${API}/fuel/missions`, {
        params: { user_id: driverId },
        withCredentials: true
      });
      setMissions(res.data.missions || []);
    } catch (err) {
      console.error('Failed to fetch missions:', err);
    }
  };

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [fetchLogs, fetchStats]);

  // ======= GROUPED FUEL FETCHERS =======
  const fetchGroups = useCallback(async () => {
    setGroupsLoading(true);
    try {
      const { start_date, end_date } = getDateRange();
      const res = await axios.get(`${API}/fuel/groups`, {
        params: { start_date, end_date, page: groupsPage, page_size: 50 },
        withCredentials: true
      });
      setGroups(res.data.groups || []);
      setGroupsTotal(res.data.total || 0);
      setGroupsTotalPages(res.data.total_pages || 0);
    } catch (err) {
      console.error('Failed to fetch fuel groups:', err);
    } finally {
      setGroupsLoading(false);
    }
  }, [getDateRange, groupsPage]);

  const fetchGroupStats = useCallback(async () => {
    try {
      const { start_date, end_date } = getDateRange();
      const res = await axios.get(`${API}/fuel/group-stats`, {
        params: { start_date, end_date },
        withCredentials: true
      });
      setGroupStats(res.data);
    } catch (err) {
      console.error('Failed to fetch group stats:', err);
    }
  }, [getDateRange]);

  const fetchVehicles = async () => {
    try {
      const res = await axios.get(`${API}/vehicles`, { withCredentials: true });
      setAllVehicles(res.data || []);
    } catch (err) {
      console.error('Failed to fetch vehicles:', err);
    }
  };

  useEffect(() => {
    if (activeTab === 'grouped') {
      fetchGroups();
      fetchGroupStats();
    }
  }, [activeTab, fetchGroups, fetchGroupStats]);

  useEffect(() => {
    if (showGroupForm && allVehicles.length === 0) {
      fetchVehicles();
    }
  }, [showGroupForm]);

  const toggleVehicle = (vehicleId) => {
    setSelectedVehicles(prev =>
      prev.includes(vehicleId) ? prev.filter(v => v !== vehicleId) : [...prev, vehicleId]
    );
  };

  const handleAddGroup = async () => {
    if (!groupMontant || parseFloat(groupMontant) <= 0) {
      setGroupError('Veuillez saisir un montant positif');
      return;
    }
    if (selectedVehicles.length === 0) {
      setGroupError('Sélectionnez au moins un véhicule');
      return;
    }
    setGroupLoading(true);
    setGroupError(null);
    setGroupSuccess(null);
    try {
      await axios.post(`${API}/fuel/group`, {
        date: groupDate,
        montant_total: parseFloat(groupMontant),
        vehicle_ids: selectedVehicles
      }, { withCredentials: true });
      setGroupSuccess('Plein groupé enregistré');
      setGroupMontant('');
      setSelectedVehicles([]);
      fetchGroups();
      fetchGroupStats();
      setTimeout(() => { setShowGroupForm(false); setGroupSuccess(null); }, 1500);
    } catch (err) {
      setGroupError(err.response?.data?.detail || 'Erreur');
    } finally {
      setGroupLoading(false);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Supprimer ce plein groupé ?')) return;
    try {
      await axios.delete(`${API}/fuel/group/${groupId}`, { withCredentials: true });
      fetchGroups();
      fetchGroupStats();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur');
    }
  };

  // ======= ALERTS FUNCTIONS =======
  const fetchAlerts = useCallback(async () => {
    setAlertsLoading(true);
    try {
      const res = await axios.get(`${API}/fuel/alerts`, {
        params: { page: alertsPage, page_size: 50 },
        withCredentials: true
      });
      setAlerts(res.data.alerts || []);
      setAlertsTotal(res.data.total || 0);
      setAlertsTotalPages(res.data.total_pages || 0);
    } catch (err) {
      console.error('Failed to fetch alerts:', err);
    } finally {
      setAlertsLoading(false);
    }
  }, [alertsPage]);

  const handleScan = async () => {
    setScanLoading(true);
    setScanResult(null);
    try {
      const res = await axios.post(`${API}/fuel/alerts/scan`, {}, { withCredentials: true });
      setScanResult(res.data);
      fetchAlerts();
    } catch (err) {
      console.error('Scan failed:', err);
      toast.error(`Analyse impossible : ${typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'vérifiez la connexion puis réessayez'}`);
    } finally {
      setScanLoading(false);
    }
  };

  const handleDismissAlert = async (alertId) => {
    try {
      await axios.delete(`${API}/fuel/alert/${alertId}`, { withCredentials: true });
      fetchAlerts();
    } catch (err) {
      alert('Erreur lors de la suppression');
    }
  };

  useEffect(() => {
    if (activeTab === 'alerts') fetchAlerts();
  }, [activeTab, fetchAlerts]);

  const exportGroupedExcel = () => {
    if (groups.length === 0) return;
    const rows = groups.map(g => ({
      'Date': g.date || '',
      'Site': g.site_name || '',
      'Montant Total (EUR)': g.montant_total ?? '',
      'Nb Véhicules': g.nb_vehicles ?? '',
      'Montant / Véhicule (EUR)': g.montant_per_vehicle ?? '',
      'Véhicules': (g.vehicles || []).map(v => v.plate_number).join(', '),
      'Type': 'Donnée estimée'
    }));
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, 'Pleins Groupés');
    if (groupStats?.by_site?.length) {
      const sr = groupStats.by_site.map(s => ({ 'Site': s.label, 'Montant Total (EUR)': s.montant, 'Nb Pleins': s.count }));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sr), 'Par Site');
    }
    if (groupStats?.by_vehicle?.length) {
      const vr = groupStats.by_vehicle.map(s => ({ 'Véhicule': s.label, 'Montant Total (EUR)': s.montant, 'Nb Pleins': s.count }));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(vr), 'Par Véhicule');
    }
    const { start_date, end_date } = getDateRange();
    XLSX.writeFile(wb, `Pleins_Groupés_${start_date}_${end_date}.xlsx`);
  };

  useEffect(() => {
    if (showAddForm && drivers.length === 0) {
      fetchDrivers();
    }
  }, [showAddForm]);

  useEffect(() => {
    if (selectedDriver) {
      fetchMissions(selectedDriver);
      setSelectedMission('');
    } else {
      setMissions([]);
    }
  }, [selectedDriver]);

  const handleAddFuel = async () => {
    if (!selectedMission || !montantTtc || parseFloat(montantTtc) <= 0) {
      setAddError('Veuillez sélectionner une mission et saisir le montant TTC');
      return;
    }
    setAddLoading(true);
    setAddError(null);
    setAddSuccess(null);
    try {
      const payload = {
        schedule_id: selectedMission,
        montant_ttc: parseFloat(montantTtc)
      };
      if (litres && parseFloat(litres) > 0) {
        payload.litres = parseFloat(litres);
      }
      await axios.post(`${API}/fuel/log`, payload, { withCredentials: true });
      setAddSuccess('Plein enregistré avec succès');
      setSelectedDriver('');
      setSelectedMission('');
      setMontantTtc('');
      setLitres('');
      setMissions([]);
      fetchLogs();
      fetchStats();
      setTimeout(() => { setShowAddForm(false); setAddSuccess(null); }, 1500);
    } catch (err) {
      setAddError(err.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setAddLoading(false);
    }
  };

  const handleDeleteLog = async (fuelLogId) => {
    if (!window.confirm('Supprimer cet enregistrement ?')) return;
    try {
      await axios.delete(`${API}/fuel/log/${fuelLogId}`, { withCredentials: true });
      fetchLogs();
      fetchStats();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const exportExcel = () => {
    if (logs.length === 0) return;

    // Main data sheet
    const rows = logs.map(l => ({
      'Date': l.date || '',
      'Site': l.site_name || '',
      'Chauffeur': l.user_name || '',
      'Véhicule': l.vehicle_plate || '',
      'Km Début': l.km_debut ?? '',
      'Km Fin': l.km_fin ?? '',
      'Distance (km)': l.distance ?? '',
      'Montant TTC (EUR)': l.montant_ttc ?? '',
      'Coût/km (EUR)': l.cout_km ?? '',
      'Litres': l.litres ?? '',
      'L/100km': l.consumption_l100 ?? ''
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, 'Historique');

    // Summary sheets from stats
    if (stats) {
      if (stats.by_site?.length) {
        const siteRows = stats.by_site.map(s => ({
          'Site': s.label,
          'Pleins': s.count,
          'Coût Total TTC (EUR)': s.cost,
          'Distance Total (km)': s.distance,
          'Coût/km (EUR)': s.cout_km ?? '',
          'Litres Total': s.litres || '',
          'Moy. L/100km': s.avg_l100 ?? ''
        }));
        const wsSite = XLSX.utils.json_to_sheet(siteRows);
        XLSX.utils.book_append_sheet(wb, wsSite, 'Par Site');
      }
      if (stats.by_driver?.length) {
        const driverRows = stats.by_driver.map(s => ({
          'Chauffeur': s.label,
          'Pleins': s.count,
          'Coût Total TTC (EUR)': s.cost,
          'Distance Total (km)': s.distance,
          'Coût/km (EUR)': s.cout_km ?? '',
          'Litres Total': s.litres || '',
          'Moy. L/100km': s.avg_l100 ?? ''
        }));
        const wsDriver = XLSX.utils.json_to_sheet(driverRows);
        XLSX.utils.book_append_sheet(wb, wsDriver, 'Par Chauffeur');
      }
      if (stats.by_vehicle?.length) {
        const vehicleRows = stats.by_vehicle.map(s => ({
          'Véhicule': s.label,
          'Pleins': s.count,
          'Coût Total TTC (EUR)': s.cost,
          'Distance Total (km)': s.distance,
          'Coût/km (EUR)': s.cout_km ?? '',
          'Litres Total': s.litres || '',
          'Moy. L/100km': s.avg_l100 ?? ''
        }));
        const wsVehicle = XLSX.utils.json_to_sheet(vehicleRows);
        XLSX.utils.book_append_sheet(wb, wsVehicle, 'Par Véhicule');
      }
    }

    const { start_date, end_date } = getDateRange();
    XLSX.writeFile(wb, `Carburant_${start_date}_${end_date}.xlsx`);
  };

  const selectedMissionData = missions.find(m => m.schedule_id === selectedMission);

  return (
    <Layout user={user} activeModule={activeModule}>
      <div className="space-y-6" data-testid="fuel-tracking-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="fuel-tracking-title">Suivi Carburant</h1>
            <p className="text-sm text-gray-500 mt-1">Consommation et pleins des véhicules</p>
          </div>
          <div className="flex gap-2">
            {activeTab === 'logs' && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={exportExcel}
                  disabled={logs.length === 0}
                  data-testid="fuel-export-btn"
                >
                  <Download className="w-4 h-4 mr-1" /> Export Excel
                </Button>
                <Button
                  size="sm"
                  onClick={() => { setShowAddForm(true); setAddError(null); setAddSuccess(null); }}
                  data-testid="fuel-add-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> Ajouter un plein
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Date navigation */}
        <Card>
          <CardContent className="p-3">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="flex gap-1">
                {['day', 'week', 'month'].map(mode => (
                  <Button
                    key={mode}
                    variant={viewMode === mode ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => { setViewMode(mode); setLogsPage(1); }}
                    data-testid={`fuel-view-${mode}`}
                  >
                    {mode === 'day' ? 'Jour' : mode === 'week' ? 'Semaine' : 'Mois'}
                  </Button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="fuel-nav-prev">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm font-medium min-w-[200px] text-center capitalize" data-testid="fuel-date-label">
                  {getDateLabel()}
                </span>
                <Button variant="ghost" size="sm" onClick={() => navigate(1)} data-testid="fuel-nav-next">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setCurrentDate(new Date()); setLogsPage(1); }}
              >
                Aujourd'hui
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <div className="flex gap-1 border-b">
          {[
            { id: 'logs', label: 'Plein Mission' },
            { id: 'grouped', label: 'Plein Groupé' },
            { id: 'alerts', label: `Alertes${alertsTotal > 0 ? ` (${alertsTotal})` : ''}` },
            { id: 'stats', label: 'Statistiques' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-slate-800 text-slate-800'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid={`fuel-tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Add Fuel Form Modal - Only on Plein Mission tab */}
        {showAddForm && activeTab === 'logs' && (
          <Card className="border-blue-200 bg-blue-50/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Fuel className="w-5 h-5 text-blue-600" />
                Ajouter un plein
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {addError && (
                <Alert variant="destructive"><AlertDescription>{addError}</AlertDescription></Alert>
              )}
              {addSuccess && (
                <Alert className="border-green-500 bg-green-50"><AlertDescription className="text-green-700">{addSuccess}</AlertDescription></Alert>
              )}

              {/* Select driver */}
              <div>
                <Label>Chauffeur *</Label>
                <select
                  value={selectedDriver}
                  onChange={(e) => setSelectedDriver(e.target.value)}
                  className="w-full mt-1 px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-blue-500"
                  data-testid="fuel-driver-select"
                >
                  <option value="">Sélectionner un chauffeur</option>
                  {drivers.map(d => (
                    <option key={d.user_id} value={d.user_id}>{d.name || `${d.first_name} ${d.last_name}`}</option>
                  ))}
                </select>
              </div>

              {/* Missions for the driver */}
              {selectedDriver && (
                <div>
                  <Label>Mission *</Label>
                  {missions.length === 0 ? (
                    <p className="text-sm text-gray-500 mt-1">Aucune mission avec kilomètres trouvée pour ce chauffeur</p>
                  ) : (
                    <select
                      value={selectedMission}
                      onChange={(e) => setSelectedMission(e.target.value)}
                      className="w-full mt-1 px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-blue-500"
                      data-testid="fuel-mission-select"
                    >
                      <option value="">Sélectionner une mission</option>
                      {missions.filter(m => !m.has_fuel_log).map(m => (
                        <option key={m.schedule_id} value={m.schedule_id}>
                          {m.date} - {m.vehicle_plate} - {m.distance} km ({m.km_debut} → {m.km_fin})
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {/* Auto-filled mission info */}
              {selectedMissionData && (
                <div className="bg-white rounded-lg border p-3 space-y-1 text-sm">
                  <div className="grid grid-cols-2 gap-2">
                    <div><span className="text-gray-500">Véhicule :</span> <span className="font-medium">{selectedMissionData.vehicle_plate}</span></div>
                    <div><span className="text-gray-500">Date :</span> <span className="font-medium">{selectedMissionData.date}</span></div>
                    <div><span className="text-gray-500">Km début :</span> <span className="font-medium">{selectedMissionData.km_debut?.toLocaleString()}</span></div>
                    <div><span className="text-gray-500">Km fin :</span> <span className="font-medium">{selectedMissionData.km_fin?.toLocaleString()}</span></div>
                    <div><span className="text-gray-500">Distance :</span> <span className="font-semibold text-blue-700">{selectedMissionData.distance} km</span></div>
                    <div><span className="text-gray-500">Site :</span> <span className="font-medium">{selectedMissionData.site_name}</span></div>
                  </div>
                </div>
              )}

              {/* Montant TTC + Litres inputs */}
              {selectedMission && (
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="fuel-montant">Montant TTC (EUR) *</Label>
                    <Input
                      id="fuel-montant"
                      type="number"
                      min="0.01"
                      step="0.01"
                      placeholder="Ex: 85.50"
                      value={montantTtc}
                      onChange={(e) => setMontantTtc(e.target.value)}
                      className="mt-1"
                      data-testid="fuel-montant-input"
                    />
                    {montantTtc && parseFloat(montantTtc) > 0 && selectedMissionData?.distance > 0 && (
                      <p className="text-sm text-blue-600 mt-1 font-medium">
                        Coût/km : {(parseFloat(montantTtc) / selectedMissionData.distance).toFixed(4)} EUR/km
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="fuel-litres">Litres <span className="text-gray-400 font-normal">(optionnel)</span></Label>
                    <Input
                      id="fuel-litres"
                      type="number"
                      min="0.1"
                      step="0.1"
                      placeholder="Ex: 45.5"
                      value={litres}
                      onChange={(e) => setLitres(e.target.value)}
                      className="mt-1"
                      data-testid="fuel-litres-input"
                    />
                    {litres && parseFloat(litres) > 0 && selectedMissionData?.distance > 0 && (
                      <p className="text-sm text-gray-500 mt-1">
                        Consommation : {((parseFloat(litres) / selectedMissionData.distance) * 100).toFixed(2)} L/100km
                      </p>
                    )}
                  </div>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => { setShowAddForm(false); setAddError(null); setAddSuccess(null); setSelectedDriver(''); setSelectedMission(''); setMontantTtc(''); setLitres(''); setMissions([]); }}
                  data-testid="fuel-cancel-btn"
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleAddFuel}
                  disabled={addLoading || !selectedMission || !montantTtc || parseFloat(montantTtc) <= 0}
                  data-testid="fuel-submit-btn"
                >
                  {addLoading ? 'Enregistrement...' : 'Enregistrer le plein'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Logs tab */}
        {activeTab === 'logs' && (
          <div>
            {/* Summary KPIs */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-slate-800" data-testid="fuel-kpi-count">{logsTotal}</p>
                  <p className="text-xs text-gray-500">Pleins enregistrés</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-blue-600" data-testid="fuel-kpi-cost">
                    {logs.reduce((s, l) => s + (l.montant_ttc || 0), 0).toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-500">Coût total TTC (EUR)</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-green-600" data-testid="fuel-kpi-distance">
                    {logs.reduce((s, l) => s + (l.distance || 0), 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-500">Km parcourus</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-orange-600" data-testid="fuel-kpi-avg">
                    {stats?.avg_cout_km?.toFixed(4) ?? '-'}
                  </p>
                  <p className="text-xs text-gray-500">Coût moy. EUR/km</p>
                </CardContent>
              </Card>
            </div>

            {/* Table */}
            {logsLoading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-800"></div>
              </div>
            ) : logs.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-gray-500">
                  <Fuel className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Aucun plein enregistré pour cette période</p>
                </CardContent>
              </Card>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="fuel-logs-table">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left p-3 font-medium text-gray-600">Date</th>
                      <th className="text-left p-3 font-medium text-gray-600">Site</th>
                      <th className="text-left p-3 font-medium text-gray-600">Chauffeur</th>
                      <th className="text-left p-3 font-medium text-gray-600">Véhicule</th>
                      <th className="text-right p-3 font-medium text-gray-600">Distance</th>
                      <th className="text-right p-3 font-medium text-gray-600">Montant TTC</th>
                      <th className="text-right p-3 font-medium text-gray-600">EUR/km</th>
                      <th className="text-right p-3 font-medium text-gray-400">Litres</th>
                      <th className="text-right p-3 font-medium text-gray-400">L/100</th>
                      <th className="p-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log, i) => (
                      <tr key={log.fuel_log_id || i} className="border-b hover:bg-gray-50" data-testid={`fuel-log-row-${i}`}>
                        <td className="p-3">{log.date}</td>
                        <td className="p-3 text-gray-600">{log.site_name || '-'}</td>
                        <td className="p-3">{log.user_name || '-'}</td>
                        <td className="p-3 font-mono text-xs">{log.vehicle_plate || '-'}</td>
                        <td className="p-3 text-right font-medium">{log.distance != null ? `${log.distance} km` : '-'}</td>
                        <td className="p-3 text-right font-semibold text-blue-700">{log.montant_ttc != null ? `${log.montant_ttc.toFixed(2)} EUR` : '-'}</td>
                        <td className="p-3 text-right">
                          {log.cout_km != null ? (
                            <span className="font-semibold text-slate-800">{log.cout_km.toFixed(4)}</span>
                          ) : '-'}
                        </td>
                        <td className="p-3 text-right text-gray-400">{log.litres ? `${log.litres.toFixed(1)} L` : '-'}</td>
                        <td className="p-3 text-right text-gray-400">
                          {log.consumption_l100 != null ? log.consumption_l100.toFixed(2) : '-'}
                        </td>
                        <td className="p-3">
                          <button
                            onClick={() => handleDeleteLog(log.fuel_log_id)}
                            className="text-gray-400 hover:text-red-500 transition-colors"
                            title="Supprimer"
                            data-testid={`fuel-delete-${i}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {logsTotalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-gray-500">
                  Page {logsPage} / {logsTotalPages} ({logsTotal} résultats)
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={logsPage <= 1}
                    onClick={() => setLogsPage(p => p - 1)}
                    data-testid="fuel-page-prev"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={logsPage >= logsTotalPages}
                    onClick={() => setLogsPage(p => p + 1)}
                    data-testid="fuel-page-next"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ======= GROUPED FUEL TAB ======= */}
        {activeTab === 'grouped' && (
          <div data-testid="fuel-grouped-tab">
            {/* Add Group Form */}
            {showGroupForm && (
              <Card className="border-amber-200 bg-amber-50/30 mb-4">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Users className="w-5 h-5 text-amber-600" />
                    Ajouter un plein groupé
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {groupError && <Alert variant="destructive"><AlertDescription>{groupError}</AlertDescription></Alert>}
                  {groupSuccess && <Alert className="border-green-500 bg-green-50"><AlertDescription className="text-green-700">{groupSuccess}</AlertDescription></Alert>}

                  <div className="bg-amber-100/60 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 flex items-start gap-2">
                    <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>Les pleins groupés sont des <strong>données estimées</strong>. Le montant est réparti équitablement entre les véhicules sélectionnés.</span>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="group-date">Date *</Label>
                      <Input id="group-date" type="date" value={groupDate} onChange={(e) => setGroupDate(e.target.value)} className="mt-1" data-testid="group-date-input" />
                    </div>
                    <div>
                      <Label htmlFor="group-montant">Montant total (EUR) *</Label>
                      <Input id="group-montant" type="number" min="0.01" step="0.01" placeholder="Ex: 250.00" value={groupMontant} onChange={(e) => setGroupMontant(e.target.value)} className="mt-1" data-testid="group-montant-input" />
                    </div>
                  </div>

                  <div>
                    <Label>Véhicules * ({selectedVehicles.length} sélectionné{selectedVehicles.length !== 1 ? 's' : ''})</Label>
                    {groupMontant && parseFloat(groupMontant) > 0 && selectedVehicles.length > 0 && (
                      <p className="text-sm text-amber-700 font-medium mt-1">
                        Répartition : {(parseFloat(groupMontant) / selectedVehicles.length).toFixed(2)} EUR / véhicule
                      </p>
                    )}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2 max-h-48 overflow-y-auto">
                      {allVehicles.map(v => (
                        <label key={v.vehicle_id} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                          selectedVehicles.includes(v.vehicle_id) ? 'bg-amber-100 border-amber-400' : 'bg-white border-gray-200 hover:border-gray-300'
                        }`} data-testid={`group-vehicle-${v.vehicle_id}`}>
                          <input type="checkbox" checked={selectedVehicles.includes(v.vehicle_id)} onChange={() => toggleVehicle(v.vehicle_id)} className="rounded" />
                          <span className="text-sm font-mono">{v.plate_number}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-2 pt-2">
                    <Button variant="outline" onClick={() => { setShowGroupForm(false); setGroupError(null); setGroupSuccess(null); setSelectedVehicles([]); setGroupMontant(''); }} data-testid="group-cancel-btn">Annuler</Button>
                    <Button onClick={handleAddGroup} disabled={groupLoading || selectedVehicles.length === 0 || !groupMontant || parseFloat(groupMontant) <= 0} data-testid="group-submit-btn">
                      {groupLoading ? 'Enregistrement...' : 'Enregistrer le plein groupé'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Group header with actions */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <p className="text-sm text-gray-500">{groupsTotal} plein{groupsTotal !== 1 ? 's' : ''} groupé{groupsTotal !== 1 ? 's' : ''}</p>
                {groupStats?.total_montant > 0 && (
                  <span className="text-sm font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded">{groupStats.total_montant.toFixed(2)} EUR total</span>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={exportGroupedExcel} disabled={groups.length === 0} data-testid="group-export-btn">
                  <Download className="w-4 h-4 mr-1" /> Export Excel
                </Button>
                <Button size="sm" onClick={() => { setShowGroupForm(true); setGroupError(null); setGroupSuccess(null); }} data-testid="group-add-btn">
                  <Plus className="w-4 h-4 mr-1" /> Plein groupé
                </Button>
              </div>
            </div>

            {/* Groups table */}
            {groupsLoading ? (
              <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div></div>
            ) : groups.length === 0 ? (
              <Card><CardContent className="p-8 text-center text-gray-500"><Users className="w-12 h-12 mx-auto mb-3 text-gray-300" /><p>Aucun plein groupé pour cette période</p></CardContent></Card>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="fuel-groups-table">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left p-3 font-medium text-gray-600">Date</th>
                      <th className="text-left p-3 font-medium text-gray-600">Site</th>
                      <th className="text-right p-3 font-medium text-gray-600">Montant Total</th>
                      <th className="text-right p-3 font-medium text-gray-600">Véhicules</th>
                      <th className="text-right p-3 font-medium text-gray-600">EUR / Véhicule</th>
                      <th className="text-left p-3 font-medium text-gray-600">Plaques</th>
                      <th className="p-3 font-medium text-gray-600">Type</th>
                      <th className="p-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {groups.map((g, i) => (
                      <tr key={g.group_id || i} className="border-b hover:bg-gray-50" data-testid={`group-row-${i}`}>
                        <td className="p-3">{g.date}</td>
                        <td className="p-3 text-gray-600">{g.site_name || '-'}</td>
                        <td className="p-3 text-right font-semibold text-amber-700">{g.montant_total?.toFixed(2)} EUR</td>
                        <td className="p-3 text-right">{g.nb_vehicles}</td>
                        <td className="p-3 text-right font-medium">{g.montant_per_vehicle?.toFixed(2)} EUR</td>
                        <td className="p-3 font-mono text-xs text-gray-500">{(g.vehicles || []).map(v => v.plate_number).join(', ')}</td>
                        <td className="p-3"><span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200"><Info className="w-3 h-3" />Estimé</span></td>
                        <td className="p-3">
                          <button onClick={() => handleDeleteGroup(g.group_id)} className="text-gray-400 hover:text-red-500 transition-colors" title="Supprimer" data-testid={`group-delete-${i}`}><Trash2 className="w-4 h-4" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Group Pagination */}
            {groupsTotalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-gray-500">Page {groupsPage} / {groupsTotalPages} ({groupsTotal} résultats)</p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={groupsPage <= 1} onClick={() => setGroupsPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
                  <Button variant="outline" size="sm" disabled={groupsPage >= groupsTotalPages} onClick={() => setGroupsPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
                </div>
              </div>
            )}

            {/* Group Stats Summary */}
            {groupStats && (groupStats.by_site?.length > 0 || groupStats.by_vehicle?.length > 0) && (
              <div className="space-y-4 mt-6">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Résumé estimations groupées</h3>
                {groupStats.by_site?.length > 0 && (
                  <GroupStatsTable title="Par Site" data={groupStats.by_site} testId="group-stats-site" />
                )}
                {groupStats.by_vehicle?.length > 0 && (
                  <GroupStatsTable title="Par Véhicule" data={groupStats.by_vehicle} testId="group-stats-vehicle" />
                )}
              </div>
            )}
          </div>
        )}

        {/* ======= ALERTS TAB ======= */}
        {activeTab === 'alerts' && (
          <div data-testid="fuel-alerts-tab">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-gray-500">{alertsTotal} alerte{alertsTotal !== 1 ? 's' : ''} détectée{alertsTotal !== 1 ? 's' : ''}</p>
              <Button size="sm" variant="outline" onClick={handleScan} disabled={scanLoading} data-testid="fuel-scan-btn">
                <RefreshCw className={`w-4 h-4 mr-1 ${scanLoading ? 'animate-spin' : ''}`} />
                {scanLoading ? 'Analyse...' : 'Lancer une analyse'}
              </Button>
            </div>

            {scanResult && (
              <Alert className="mb-4 border-blue-200 bg-blue-50">
                <AlertDescription className="text-blue-800">
                  <strong>{scanResult.alerts_count} alerte{scanResult.alerts_count !== 1 ? 's' : ''} détectée{scanResult.alerts_count !== 1 ? 's' : ''}</strong>
                  {scanResult.alerts_count > 0 && (
                    <span className="ml-2 text-sm">
                      ({scanResult.by_type?.frequency || 0} fréquence, {scanResult.by_type?.comparison || 0} comparaison, {scanResult.by_type?.overconsumption || 0} surconsommation, {scanResult.by_type?.fuel_vs_km || 0} ratio km)
                    </span>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {alertsLoading ? (
              <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div></div>
            ) : alerts.length === 0 ? (
              <Card><CardContent className="p-8 text-center text-gray-500">
                <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>Aucune alerte détectée</p>
                <p className="text-xs mt-1">Lancez une analyse pour détecter les anomalies</p>
              </CardContent></Card>
            ) : (
              <div className="space-y-3">
                {alerts.map((a, i) => (
                  <Card key={a.alert_id || i} className={`border-l-4 ${
                    a.severity === 'high' ? 'border-l-red-500 bg-red-50/30' : 'border-l-amber-500 bg-amber-50/30'
                  }`} data-testid={`alert-row-${i}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${a.severity === 'high' ? 'text-red-500' : 'text-amber-500'}`} />
                          <div>
                            <p className="font-medium text-gray-900">{a.message}</p>
                            <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                              <span>{a.date}</span>
                              <span className={`px-1.5 py-0.5 rounded font-medium ${
                                a.type === 'frequency' ? 'bg-purple-100 text-purple-700' :
                                a.type === 'comparison' ? 'bg-blue-100 text-blue-700' :
                                a.type === 'overconsumption' ? 'bg-red-100 text-red-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {a.type === 'frequency' ? 'Fréquence' :
                                 a.type === 'comparison' ? 'Comparaison' :
                                 a.type === 'overconsumption' ? 'Surconsommation' :
                                 'Ratio km/carburant'}
                              </span>
                              <span className={`px-1.5 py-0.5 rounded font-medium ${
                                a.severity === 'high' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                              }`}>
                                {a.severity === 'high' ? 'Haute' : 'Moyenne'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <button onClick={() => handleDismissAlert(a.alert_id)} className="text-gray-400 hover:text-gray-600 p-1" title="Ignorer" data-testid={`alert-dismiss-${i}`}>
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {alertsTotalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-gray-500">Page {alertsPage} / {alertsTotalPages}</p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={alertsPage <= 1} onClick={() => setAlertsPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
                  <Button variant="outline" size="sm" disabled={alertsPage >= alertsTotalPages} onClick={() => setAlertsPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Stats tab */}
        {activeTab === 'stats' && (
          <div className="space-y-6">
            {statsLoading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-800"></div>
              </div>
            ) : !stats || (!stats.by_site?.length && !stats.by_driver?.length && !stats.by_vehicle?.length) ? (
              <Card>
                <CardContent className="p-8 text-center text-gray-500">
                  <TrendingUp className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Aucune donnée pour cette période</p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Global cost stat */}
                {stats.total_cost > 0 && (
                  <Card className="border-slate-200 bg-slate-50">
                    <CardContent className="p-4">
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-center">
                        <div>
                          <p className="text-sm text-gray-500 mb-1">Coût total TTC</p>
                          <p className="text-3xl font-bold text-blue-700" data-testid="fuel-total-cost">
                            {stats.total_cost.toFixed(2)} <span className="text-lg font-normal text-gray-500">EUR</span>
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500 mb-1">Coût moyen / km</p>
                          <p className="text-3xl font-bold text-slate-800" data-testid="fuel-avg-cout-km">
                            {stats.avg_cout_km?.toFixed(4) ?? '-'} <span className="text-lg font-normal text-gray-500">EUR/km</span>
                          </p>
                        </div>
                        {stats.global_avg && (
                          <div>
                            <p className="text-sm text-gray-400 mb-1">Moy. L/100km</p>
                            <p className="text-2xl font-bold text-gray-400" data-testid="fuel-global-avg">
                              {stats.global_avg.toFixed(2)}
                            </p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* By Site */}
                {stats.by_site?.length > 0 && (
                  <StatsTable title="Par Site" data={stats.by_site} testId="fuel-stats-site" />
                )}

                {/* By Driver */}
                {stats.by_driver?.length > 0 && (
                  <StatsTable title="Par Chauffeur" data={stats.by_driver} testId="fuel-stats-driver" />
                )}

                {/* By Vehicle */}
                {stats.by_vehicle?.length > 0 && (
                  <StatsTable title="Par Véhicule" data={stats.by_vehicle} testId="fuel-stats-vehicle" />
                )}
              </>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

const StatsTable = ({ title, data, testId }) => (
  <Card>
    <CardHeader className="pb-2">
      <CardTitle className="text-base">{title}</CardTitle>
    </CardHeader>
    <CardContent>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid={testId}>
          <thead>
            <tr className="border-b bg-gray-50">
              <th className="text-left p-2 font-medium text-gray-600">{title.replace('Par ', '')}</th>
              <th className="text-right p-2 font-medium text-gray-600">Pleins</th>
              <th className="text-right p-2 font-medium text-gray-600">Coût TTC (EUR)</th>
              <th className="text-right p-2 font-medium text-gray-600">Distance (km)</th>
              <th className="text-right p-2 font-medium text-gray-600">EUR/km</th>
              <th className="text-right p-2 font-medium text-gray-400">Litres</th>
              <th className="text-right p-2 font-medium text-gray-400">L/100km</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, i) => (
              <tr key={i} className="border-b hover:bg-gray-50">
                <td className="p-2 font-medium">{row.label}</td>
                <td className="p-2 text-right">{row.count}</td>
                <td className="p-2 text-right font-semibold text-blue-700">{row.cost?.toFixed(2)}</td>
                <td className="p-2 text-right">{row.distance?.toLocaleString()}</td>
                <td className="p-2 text-right font-semibold text-slate-800">{row.cout_km?.toFixed(4) ?? '-'}</td>
                <td className="p-2 text-right text-gray-400">{row.litres ? row.litres.toFixed(1) : '-'}</td>
                <td className="p-2 text-right text-gray-400">{row.avg_l100 != null ? row.avg_l100.toFixed(2) : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CardContent>
  </Card>
);

const GroupStatsTable = ({ title, data, testId }) => (
  <Card>
    <CardHeader className="pb-2">
      <CardTitle className="text-base flex items-center gap-2">{title} <span className="text-xs font-normal text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">Estimé</span></CardTitle>
    </CardHeader>
    <CardContent>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid={testId}>
          <thead>
            <tr className="border-b bg-gray-50">
              <th className="text-left p-2 font-medium text-gray-600">{title.replace('Par ', '')}</th>
              <th className="text-right p-2 font-medium text-gray-600">Nb Pleins</th>
              <th className="text-right p-2 font-medium text-gray-600">Montant (EUR)</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, i) => (
              <tr key={i} className="border-b hover:bg-gray-50">
                <td className="p-2 font-medium">{row.label}</td>
                <td className="p-2 text-right">{row.count}</td>
                <td className="p-2 text-right font-semibold text-amber-700">{row.montant?.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CardContent>
  </Card>
);

export default FuelTracking;
